import React, {memo, useCallback} from 'react';
import {get} from 'lodash';
import {FeatureFlag} from 'react-launch-darkly';

const NOT_ASSIGNED_LABEL = 'N/A';

const TargetingOptionsDetails = memo(({details}) => {
    const genderValue = get(details, 'gender') || NOT_ASSIGNED_LABEL;
    const minAgeValue = get(details, 'age.minAge');
    const maxAgeValue = get(details, 'age.maxAge');
    const minFollowersValue = get(details, 'followers.minFollowers');
    const maxFollowersValue = get(details, 'followers.maxFollowers');
    const followersTiers = get(details, 'followersTiers') || [];
    const locations = get(details, 'locations') || [];
    const languages = get(details, 'languages') || [];
    const categories = get(details, 'categories') || [];

    const renderAgeValue = useCallback(() => {
        if (minAgeValue && maxAgeValue) {
            return `From ${minAgeValue}, To ${maxAgeValue}`;
        }
        if (minAgeValue) {
            return `From ${minAgeValue}`;
        }
        if (maxAgeValue) {
            return `To ${maxAgeValue}`;
        }

        return NOT_ASSIGNED_LABEL;
    }, [minAgeValue, maxAgeValue]);

    const renderFollowerTiersValue = useCallback(() => {
        if (followersTiers && followersTiers.length) {
            return (
                <div className="target-followers-list">
                    {followersTiers.map(({label, description}, index) => (
                        <span key={index}>{`${label} ${description}`}</span>
                    ))}
                </div>
            );
        }

        return NOT_ASSIGNED_LABEL;
    }, [followersTiers]);

    const renderFollowerValue = useCallback(() => {
        if (minFollowersValue && maxFollowersValue) {
            return `From ${minFollowersValue}, To ${maxFollowersValue}`;
        }
        if (minFollowersValue) {
            return `From ${minFollowersValue}`;
        }
        if (maxFollowersValue) {
            return `To ${maxFollowersValue}`;
        }

        return NOT_ASSIGNED_LABEL;
    }, [minFollowersValue, maxFollowersValue]);

    const renderLocationValue = useCallback(() => {
        if (locations.length) {
            return locations.map(({name}) => name).join(',');
        }

        return NOT_ASSIGNED_LABEL;
    }, [locations]);

    const renderLanguageValue = useCallback(() => {
        if (languages.length) {
            return languages.map(({name}) => name).join(',');
        }

        return NOT_ASSIGNED_LABEL;
    }, [languages]);

    const renderCategoryValue = useCallback(() => {
        if (categories.length) {
            return (
                <>
                    {categories.map(category => (
                        <ul key={category.primaryCategoryName} className="list-unstyled">
                            <li>
                                {category.primaryCategoryName}
                                {category.subCategories && (
                                    <ul>
                                        {category.subCategories?.map(({subCategoryName}) => (
                                            <li key={subCategoryName}>{subCategoryName}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        </ul>
                    ))}
                </>
            );
        }

        return NOT_ASSIGNED_LABEL;
    }, [categories]);

    return (
        <div className="target-details">
            <div className="section-title">Target</div>
            <div className="target-details-grid">
                <div className="target-detail">
                    <div className="section-details-title">Gender</div>
                    <div className="section-details-value">{genderValue}</div>
                </div>
                <div className="target-detail">
                    <div className="section-details-title">Age</div>
                    <div className="section-details-value">{renderAgeValue()}</div>
                </div>
                <div className="target-detail">
                    <div className="section-details-title">Location</div>
                    <div className="section-details-value">{renderLocationValue()}</div>
                </div>
                <div className="target-detail">
                    <div className="section-details-title">Language</div>
                    <div className="section-details-value">{renderLanguageValue()}</div>
                </div>
                <div className="target-detail">
                    <div className="section-details-title">Categories</div>
                    <div className="section-details-value">{renderCategoryValue()}</div>
                </div>
                <div />
                <div className="target-detail">
                    <div className="section-details-title">Followers</div>
                    <div className="section-details-value">
                        <FeatureFlag
                            flagKey="budget-v2-enabled"
                            renderDefaultCallback={renderFollowerValue}
                            renderFeatureCallback={featureFlagValue => {
                                if (featureFlagValue === 'on') {
                                    return renderFollowerTiersValue();
                                }
                                return renderFollowerValue();
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

TargetingOptionsDetails.displayName = 'TargetingOptionsDetails';

export default TargetingOptionsDetails;



// WEBPACK FOOTER //
// ./components/verify_receipt_common/targeting_options_details.js